import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Box, Flex, Group, Stack, TextInput, useDebouncedCallback } from 'xen-ui';
import { ErrorText } from 'xen/components';
import { FormCurrencyInput, FormSelect, FormTextInput } from 'xen/components/form';
import { formatCurrency } from 'xen/helpers';
import { useUpdatePaymentAssignment } from '../../api/';
import { DeletePaymentAssignmentButton } from '../../components';
import { PaymentAssignmentTypes } from '../../types';
const paymentAssignmentFormSchema = z
    .object({
    amount: FormCurrencyInput.schema({ name: 'amount', label: 'Amount', required: true }),
    collectionsCheckId: z.string(),
    businessId: z.string(),
    invoiceId: z.string(),
    type: z.enum(PaymentAssignmentTypes),
    /** invoiceBalance is only used for conditional validation — value not sent to server */
    invoiceBalance: z.number(),
    /** isNonFactored is only used for conditional validation — value not sent to server */
    isNonFactored: z.boolean(),
})
    .superRefine((data, ctx) => {
    if (data.type === 'PaymentAction') {
        if (FormCurrencyInput.formatValue(data.amount) !== data.invoiceBalance) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Full pay requires a payment amount equal to the invoice balance (${formatCurrency(data.invoiceBalance)})`,
                path: ['amount'],
            });
        }
    }
    else if (data.type === 'OverPayAction') {
        if (FormCurrencyInput.formatValue(data.amount) <= data.invoiceBalance) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Over pay requires a payment amount greater than the invoice balance (${formatCurrency(data.invoiceBalance)})`,
                path: ['amount'],
            });
        }
    }
    else if (data.type === 'ShortPayAction' || data.type === 'PartialPayAction') {
        if (FormCurrencyInput.formatValue(data.amount) >= data.invoiceBalance) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${data.type === 'ShortPayAction' ? 'Short' : 'Partial'} pay requires a payment amount less than the invoice balance (${formatCurrency(data.invoiceBalance)})`,
                path: ['amount'],
            });
        }
    }
    return true;
});
export const PaymentAssignmentUpdateForm = ({ assignment, index }) => {
    const defaultValues = {
        // Leave amount field empty if value is 0
        amount: String(Number(assignment.amount) === 0 ? '' : assignment.amount),
        collectionsCheckId: String(assignment.collectionsCheckId ?? ''),
        businessId: String(assignment.businessId ?? ''),
        invoiceBalance: assignment.invoice.balance,
        invoiceId: String(assignment.invoiceId ?? ''),
        isNonFactored: assignment.invoice.nonFactored,
        type: assignment.type,
    };
    const { control, handleSubmit, formState: { dirtyFields }, } = useForm({
        resolver: zodResolver(paymentAssignmentFormSchema),
        mode: 'onChange',
        values: defaultValues,
    });
    const { mutate: updatePaymentAssignment, isError: isUpdateError } = useUpdatePaymentAssignment(assignment.collectionsCheckId);
    const onSubmit = (data) => {
        const payload = {
            amount: FormCurrencyInput.formatValue(data.amount),
            collectionsCheckId: data.collectionsCheckId,
            businessId: Number(data.businessId),
            invoiceId: Number(data.invoiceId),
            type: data.type,
        };
        const params = { id: assignment.id, data: payload };
        updatePaymentAssignment(params);
    };
    const handleFormSubmit = () => {
        handleSubmit(onSubmit)();
    };
    const handleDebouncedFormSubmit = useDebouncedCallback(handleFormSubmit, 1000);
    const handleError = (e) => {
        console.error('Form error:', e);
    };
    const isNonFactored = assignment.invoice.nonFactored;
    const assignmentActions = isNonFactored
        ? [{ value: 'NonFactoredPaymentAction', label: 'Non-factored' }]
        : [
            {
                value: 'NotSetAction',
                label: '(Select an action)',
            },
            { value: 'ShortPayAction', label: 'Short pay' },
            { value: 'PartialPayAction', label: 'Partial pay' },
            { value: 'PaymentAction', label: 'Full pay' },
            { value: 'OverPayAction', label: 'Over pay' },
        ];
    const isLabelTextHidden = index > 0;
    const isProcessed = assignment.state === 'processed';
    return (_jsx(Form, { control: control, onError: handleError, onSubmit: handleFormSubmit, children: _jsxs(Stack, { gap: "xs", children: [isUpdateError && (_jsx(ErrorText, { message: `Error: Could not update assigned invoice ${assignment.invoice.invoiceNumber}` })), _jsxs(Group, { align: "flex-end", wrap: "nowrap", children: [_jsxs(Box, { flex: 1, children: [_jsx(TextInput, { defaultValue: String(assignment.invoice.invoiceNumber), disabled: true, hideLabel: isLabelTextHidden, label: "Invoice Number", name: "invoiceNumber", readOnly: true }), _jsx(FormTextInput, { control: control, defaultValue: String(assignment.invoice.id), label: "Invoice ID", name: "invoiceId", type: "hidden" })] }), _jsx(Box, { flex: 1, children: _jsx(TextInput, { defaultValue: formatCurrency(isProcessed ? assignment.invoice.faceValue : assignment.invoice.balance), disabled: true, hideLabel: isLabelTextHidden, label: isProcessed ? 'Invoice Amount' : 'Invoice Balance', name: "invoiceBalanceOrValue", readOnly: true }) }), _jsxs(Box, { flex: 1, children: [_jsx(TextInput, { defaultValue: String(assignment.invoice.debtorName), disabled: true, hideLabel: isLabelTextHidden, label: "Invoice Debtor", name: "invoiceDebtorName", readOnly: true }), _jsx(FormTextInput, { control: control, defaultValue: String(assignment.business.id), label: "Business ID", name: "businessId", type: "hidden" })] }), _jsx(Box, { flex: 1, children: _jsx(FormSelect, { control: control, data: assignmentActions, defaultValue: assignment.type, disabled: isNonFactored || isProcessed, error: dirtyFields.type ? undefined : assignment.errors.type, hideLabel: isLabelTextHidden, label: "Payment Action", name: "type", onChange: handleFormSubmit, required: true }) }), _jsx(Box, { flex: 1, children: _jsx(FormCurrencyInput, { control: control, disabled: isProcessed, hideLabel: isLabelTextHidden, label: "Posting Amount", name: "amount", onChange: handleDebouncedFormSubmit, required: true }) }), _jsx(Flex, { alignSelf: "flex-end", py: "0.25rem", children: _jsx(DeletePaymentAssignmentButton, { assignment: assignment, disabled: isProcessed }) })] })] }) }));
};
